import { Link } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '@/api';
import { WBSButton } from '@/common/WBSButton';
import {
  type BoardTarget,
  ModalParagraph,
  StudentBoardModal,
  StudentInfoBlock,
  TargetSelect,
  targetLabel,
  useAdoptionErrorContent,
} from './StudentBoardModal';
import {
  type StudentAdoptionInfo,
  useStudentAdoptionInfo,
} from './useStudentAdoptionInfo';

interface AdoptStudentModalProps {
  /** The student to adopt. `null` renders nothing — this is the open switch. */
  studentId: number | null;
  onClose: () => void;
  /** Fired once the adoption has landed, so the board can refresh behind it. */
  onAdopted?: () => void;
}

/**
 * Legacy's two-step adopt popup: `verify()` renders the confirm body (or an
 * error popup instead of it) and `confirm()` replaces it with the success body
 * — `AdoptStudentInteractivePopup.php:17-58`, popups 21902 and 21903.
 *
 * The confirm step is an INPUT, not a decoration: legacy's body carries a
 * `<select name="web_ad_id">` over the student's original matched targets and a
 * hidden `campaign_id`, and those become the adoption record's attribution
 * (`adoption_info.php:41-67` -> `StudentBoardInteractivePopupAjax.php:67-84`).
 */
export function AdoptStudentModal({
  studentId,
  onClose,
  onAdopted,
}: AdoptStudentModalProps) {
  const { t } = useTranslation();
  // The payload snapshot the success body reads. Held rather than re-read: the
  // student is off the board once this is set, so `/adoption-info` would answer
  // 409/404 if it were asked again.
  const [adoptedInfo, setAdoptedInfo] = useState<StudentAdoptionInfo | null>(
    null,
  );
  const [selectedTargetId, setSelectedTargetId] = useState<number | null>(null);
  const [submitError, setSubmitError] = useState<unknown>(null);
  const [openedFor, setOpenedFor] = useState<number | null>(studentId);
  // Which student the modal is showing RIGHT NOW, readable from a settled
  // request. `POST /adopt` is slower than a click, so a helper can close this
  // modal and open another one before their first adoption lands; without the
  // token that continuation writes the first student's success body into the
  // second student's modal.
  const openSession = useRef<number | null>(studentId);
  // Synchronous single-flight. `isPending` only disables the button one render
  // after the click, so a double click fires two adoptions and the second gets
  // a 409 it did not earn.
  const submitting = useRef(false);

  // Reopening for a different student must not inherit the last one's success
  // body or target choice. Adjusting state during render (rather than in an
  // effect) keeps the first paint of the new modal correct.
  if (openedFor !== studentId) {
    setOpenedFor(studentId);
    setAdoptedInfo(null);
    setSelectedTargetId(null);
    setSubmitError(null);
  }

  useEffect(() => {
    openSession.current = studentId;
  }, [studentId]);

  const { info, isLoading, error } = useStudentAdoptionInfo(
    studentId,
    adoptedInfo === null,
  );
  const { mutateAsync: adoptStudent, isPending } =
    api.adoptStudent.useMutation();

  const failure = submitError ?? error;
  const errorContent = useAdoptionErrorContent(
    failure,
    info?.display_name ?? '',
  );

  if (studentId === null) return null;

  // Success first: the adoption has landed, so nothing that follows can undo it.
  if (adoptedInfo !== null) {
    return (
      <StudentBoardModal
        title={t('lessons.student-adopted')}
        onClose={onClose}
        footer={
          <WBSButton alternate onClick={onClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>
          {t('wbs.s-is-now-your-student', { name: adoptedInfo.display_name })}
        </ModalParagraph>
        {/* Popup 21903's body is conditional: the
            [student_spiritual_completion_message] block renders only when the
            student carries `spiritual_question_completed`
            (AdoptStudentInteractivePopup.php:60-67). 38 of the 153 eligible
            board students trigger it — a quarter of adoptions, not an edge case.
            The anchor legacy wraps around "student's Profile" moves OUT of the
            sentence deliberately: PT_BR post 23022's translated sentence carries
            no markup at all, so an inline <Trans> split would have no PT_BR
            target to bind to. */}
        {adoptedInfo.spiritual_journey_completed && (
          <>
            <ModalParagraph>
              {t('wbs.s-has-completed-the-spiritual-journey-questions', {
                name: adoptedInfo.display_name,
              })}
            </ModalParagraph>
            <Link
              to="/members/$memberId/"
              params={{ memberId: String(adoptedInfo.student_id) }}
            >
              {t('wbs.view-students-profile')}
            </Link>
          </>
        )}
      </StudentBoardModal>
    );
  }

  // A typed 4xx from `/adoption-info` arrives INSTEAD of the payload, so the
  // confirm body never renders and the modal opens straight into the error —
  // legacy's own shape (AdoptStudentInteractivePopup.php:20-21). `POST /adopt`'s
  // refusals land in the same place, so there is one error path and not two.
  if (failure) {
    return (
      <StudentBoardModal
        title={errorContent.title ?? t('wbs.adoption-confirmation')}
        onClose={onClose}
        footer={
          <WBSButton alternate onClick={onClose}>
            {t('wbs.close')}
          </WBSButton>
        }
      >
        <ModalParagraph>{errorContent.body}</ModalParagraph>
      </StudentBoardModal>
    );
  }

  if (isLoading || !info) {
    return (
      <StudentBoardModal
        title={t('wbs.adoption-confirmation')}
        onClose={onClose}
      >
        <ModalParagraph>{t('wbs.loading')}</ModalParagraph>
      </StudentBoardModal>
    );
  }

  // The picker's population is ORIGINAL, not available: legacy keeps the
  // passed-on targets and marks them (adoption_info.php:50-60), where pass-on
  // drops them. `is_original` is true on all 151 matched rows over 133 students,
  // 18 of whom carry two — so a scalar would lose a real choice.
  const targets = info.original_targets;
  // Re-resolved against the CURRENT list rather than trusted: the payload
  // refetches on window focus, and a target that has since been passed on by
  // another leader must not still be the one posted.
  const postedTargetId =
    targets.find((target) => target.target_id === selectedTargetId)
      ?.target_id ??
    targets[0]?.target_id ??
    null;

  const handleAdopt = async (): Promise<void> => {
    if (submitting.current) return;
    submitting.current = true;
    const submittedFor = studentId;
    setSubmitError(null);
    try {
      await adoptStudent({
        body: {
          user_id: studentId,
          // Exactly one attribution, or neither. A matched campaign suppresses
          // the target picker entirely (the rule in StudentInfoBlock), and
          // posting both is AdoptionTargetConflictError; posting neither is the
          // common case and is accepted (spec §5.4 check_posted_targets).
          campaign_id: info.matched_campaign?.campaign_id,
          web_ad_id:
            info.matched_campaign === null && postedTargetId !== null
              ? postedTargetId
              : undefined,
        },
      });
    } catch (failed) {
      if (openSession.current === submittedFor) setSubmitError(failed);
      return;
    } finally {
      submitting.current = false;
    }
    // The write landed either way, so the board is stale either way; only the
    // modal body is conditional on this still being the student on screen.
    onAdopted?.();
    if (openSession.current === submittedFor) setAdoptedInfo(info);
  };

  return (
    <StudentBoardModal
      title={t('wbs.adoption-confirmation')}
      onClose={onClose}
      footer={
        <WBSButton
          onClick={() => {
            void handleAdopt();
          }}
          isLoading={isPending}
        >
          {t('wbs.adopt-this-student')}
        </WBSButton>
      }
    >
      <ModalParagraph>
        {t('wbs.are-you-sure-you-want-to-adopt-this-student')}
      </ModalParagraph>
      {/* [special_message_by_type type="student-postal"] in popup 21902 — the
          block renders only for a postal student, whom the adopting helper has
          to post courses to themselves. */}
      {info.profile_type === 'POSTAL_STUDENT' && (
        <ModalParagraph>{t('wbs.adopt-postal-reminder')}</ModalParagraph>
      )}
      <StudentInfoBlock
        info={info}
        targets={targets}
        renderTargets={(options: readonly BoardTarget[]) => (
          <TargetSelect
            aria-label={t('wbs.matched-web-ad-targets')}
            value={String(postedTargetId ?? '')}
            onChange={(event) =>
              setSelectedTargetId(Number(event.target.value))
            }
          >
            {options.map((target) => (
              <option key={target.target_id} value={String(target.target_id)}>
                {target.has_passed_on
                  ? `${targetLabel(target)} ${t('wbs.passed-on-suffix')}`
                  : targetLabel(target)}
              </option>
            ))}
          </TargetSelect>
        )}
      />
    </StudentBoardModal>
  );
}
